/**
 * Settings > Appearance (net-new V2 feature, client-only): theme, accent and density on the
 * left, the device-level toggles on the right. Everything persists through useUiStore /
 * useExportSummaryPref, so it holds no state of its own.
 */
import { cn } from '../../../lib/utils';
import { useUiStore, type V2Accent, type V2Density, type V2Theme } from '../../theme/uiStore';
import { useExportSummaryPref } from '../../lib/useExportSummaryPref';
import { Segmented } from '../ui/segmented';
import { SwitchRow } from '../SwitchRow';
import { IconMoon, IconSun } from '../icons';
import { FieldLabel, Section } from './SettingsFrame';

/** Accent key -> swatch fill color (matches AppearancePopover ACCENTS). */
const ACCENTS: Array<[V2Accent, string]> = [
  ['lime', '#b8ea3a'],
  ['blue', '#4f8cff'],
  ['cyan', '#2bd4e6'],
];

export default function AppearancePanel() {
  const theme = useUiStore((s) => s.theme);
  const accent = useUiStore((s) => s.accent);
  const density = useUiStore((s) => s.density);
  const setTheme = useUiStore((s) => s.setTheme);
  const setAccent = useUiStore((s) => s.setAccent);
  const setDensity = useUiStore((s) => s.setDensity);
  const exportCostConfirmEnabled = useUiStore((s) => s.exportCostConfirmEnabled);
  const setExportCostConfirmEnabled = useUiStore((s) => s.setExportCostConfirmEnabled);
  const pdfViewerEnabled = useUiStore((s) => s.pdfViewerEnabled);
  const setPdfViewerEnabled = useUiStore((s) => s.setPdfViewerEnabled);
  const [showExportSummary, toggleExportSummary] = useExportSummaryPref(); // B-7202

  return (
    <Section title="Appearance" sub="V2 interface theme — saved on this device.">
      <div className="grid grid-cols-1 gap-x-10 gap-y-5 lg:grid-cols-2">
        <div className="space-y-5 max-w-[440px]">
          <div>
            <FieldLabel>Theme</FieldLabel>
            <Segmented<V2Theme>
              value={theme}
              onValueChange={setTheme}
              fullWidth
              options={[
                { value: 'dark', label: 'Dark', icon: <IconMoon /> },
                { value: 'light', label: 'Light', icon: <IconSun /> },
              ]}
            />
          </div>

          <div>
            <FieldLabel>Signature accent</FieldLabel>
            <div className="flex gap-2">
              {ACCENTS.map(([key, color]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setAccent(key)}
                  aria-label={`${key} accent`}
                  aria-pressed={accent === key}
                  className={cn(
                    'grid h-[34px] w-full place-items-center rounded-[var(--r-md)] border border-[var(--bd)]',
                    'shadow-[inset_0_1px_0_var(--hi-top)]',
                    accent === key && 'shadow-[0_0_0_2px_var(--bg-2),0_0_0_4px_var(--accent)]',
                  )}
                  style={{
                    background: `linear-gradient(180deg, color-mix(in oklab, ${color} 84%, white), ${color})`,
                  }}
                >
                  <span className="text-[10px] font-semibold capitalize text-black/55">{key}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <FieldLabel>Density</FieldLabel>
            <Segmented<V2Density>
              value={density}
              onValueChange={setDensity}
              fullWidth
              options={[
                { value: 'compact', label: 'Compact' },
                { value: 'regular', label: 'Regular' },
                { value: 'comfy', label: 'Comfy' },
              ]}
            />
          </div>
        </div>

        <div className="space-y-5">
          <SwitchRow
            label="Confirm export cost"
            hint="Show a confirmation with the credit cost before each ESX export."
            checked={exportCostConfirmEnabled}
            onCheckedChange={setExportCostConfirmEnabled}
          />

          <SwitchRow
            label="Export summary"
            hint="Before each ESX download, show the lines to review and the next steps, and email them to you after you download."
            checked={showExportSummary ?? true}
            onCheckedChange={toggleExportSummary}
          />

          <SwitchRow
            label="Source PDF viewer"
            hint="On an imported line, add a “View in source PDF” button that opens the original report page with the line highlighted."
            checked={pdfViewerEnabled}
            onCheckedChange={setPdfViewerEnabled}
          />
        </div>
      </div>
    </Section>
  );
}
